import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  BookOpen, Plus, Edit, Search, ArrowLeft, ChevronRight, ChevronDown,
  Calculator, TrendingUp, TrendingDown, Building, Wallet
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { AccountingAccount } from "@/types/billing";

const ACCOUNT_TYPES = [
  { value: 'asset', label: 'Activo', icon: Building, color: 'blue' },
  { value: 'liability', label: 'Pasivo', icon: Wallet, color: 'red' },
  { value: 'equity', label: 'Patrimonio', icon: Calculator, color: 'purple' },
  { value: 'income', label: 'Ingreso', icon: TrendingUp, color: 'green' },
  { value: 'expense', label: 'Gasto', icon: TrendingDown, color: 'orange' },
];

const ACCOUNT_GROUPS = [
  { code: '1', name: 'Financiacion Basica', type: 'equity' },
  { code: '2', name: 'Activo No Corriente', type: 'asset' },
  { code: '3', name: 'Existencias', type: 'asset' },
  { code: '4', name: 'Acreedores y Deudores', type: 'liability' },
  { code: '5', name: 'Cuentas Financieras', type: 'asset' },
  { code: '6', name: 'Compras y Gastos', type: 'expense' },
  { code: '7', name: 'Ventas e Ingresos', type: 'income' },
];

const emptyAccount: Partial<AccountingAccount> = {
  code: '',
  name: '',
  account_type: 'expense',
  is_debit_normal: true,
  is_header: false,
  is_active: true,
};

export default function ChartOfAccounts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Partial<AccountingAccount> | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['1', '2', '3', '4', '5', '6', '7']);

  // Fetch accounts
  const { data: accounts = [], isLoading } = useQuery<AccountingAccount[]>({
    queryKey: ["accounting-accounts"],
    queryFn: async () => {
      const { data, error } = await api
        .from("accounting_accounts")
        .select("*")
        .order("code");
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Filter and group accounts
  const filteredAccounts = useMemo(() => {
    if (!search) return accounts;
    const s = search.toLowerCase();
    return accounts.filter(a =>
      a.code.toLowerCase().includes(s) ||
      a.name.toLowerCase().includes(s)
    );
  }, [accounts, search]);

  const groupedAccounts = useMemo(() => {
    const groups: Record<string, AccountingAccount[]> = {};
    ACCOUNT_GROUPS.forEach(g => { groups[g.code] = []; });

    filteredAccounts.forEach(account => {
      const groupCode = account.code.charAt(0);
      if (groups[groupCode]) {
        groups[groupCode].push(account);
      }
    });

    return groups;
  }, [filteredAccounts]);

  // Stats
  const stats = useMemo(() => {
    const totalAccounts = accounts.length;
    const activeAccounts = accounts.filter(a => a.is_active).length;
    const withBalance = accounts.filter(a => a.balance !== 0).length;
    const totalBalance = accounts.reduce((sum, a) => sum + (a.balance || 0), 0);
    return { totalAccounts, activeAccounts, withBalance, totalBalance };
  }, [accounts]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (account: Partial<AccountingAccount>) => {
      // Determine account_group from code
      const account_group = account.code?.charAt(0) || '';

      if (account.id) {
        const { error } = await api
          .from("accounting_accounts")
          .update({
            ...account,
            account_group,
            updated_at: new Date().toISOString(),
          })
          .eq("id", account.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from("accounting_accounts")
          .insert({
            ...account,
            account_group,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounting-accounts"] });
      setDialogOpen(false);
      setEditingAccount(null);
      toast.success(editingAccount?.id ? "Cuenta actualizada" : "Cuenta creada");
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleNew = () => {
    setEditingAccount({ ...emptyAccount });
    setDialogOpen(true);
  };

  const handleEdit = (account: AccountingAccount) => {
    setEditingAccount(account);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingAccount?.code || !editingAccount?.name) {
      toast.error("Codigo y nombre son obligatorios");
      return;
    }
    saveMutation.mutate(editingAccount);
  };

  const toggleGroup = (code: string) => {
    setExpandedGroups(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getTypeInfo = (type: string) => {
    return ACCOUNT_TYPES.find(t => t.value === type) || ACCOUNT_TYPES[0];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BookOpen className="w-8 h-8 text-indigo-600" />
                Plan General Contable
              </h1>
              <p className="text-muted-foreground">Gestion de cuentas contables (PGC)</p>
            </div>
          </div>
          <Button onClick={handleNew}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Cuenta
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalAccounts}</p>
                  <p className="text-sm text-muted-foreground">Total cuentas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Calculator className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.activeAccounts}</p>
                  <p className="text-sm text-muted-foreground">Activas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.withBalance}</p>
                  <p className="text-sm text-muted-foreground">Con movimientos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Wallet className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalBalance)}</p>
                  <p className="text-sm text-muted-foreground">Balance total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por codigo o nombre..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Accounts Tree */}
        <div className="space-y-2">
          {isLoading ? (
            <Card>
              <CardContent className="py-8 text-center">Cargando...</CardContent>
            </Card>
          ) : (
            ACCOUNT_GROUPS.map(group => {
              const groupAccounts = groupedAccounts[group.code] || [];
              const isExpanded = expandedGroups.includes(group.code);
              const typeInfo = getTypeInfo(group.type);

              return (
                <Card key={group.code}>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleGroup(group.code)}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/50 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                            <div className={`w-8 h-8 rounded flex items-center justify-center bg-${typeInfo.color}-100`}>
                              <span className="font-bold text-lg">{group.code}</span>
                            </div>
                            <div>
                              <CardTitle className="text-base">{group.name}</CardTitle>
                              <p className="text-xs text-muted-foreground">{groupAccounts.length} cuentas</p>
                            </div>
                          </div>
                          <Badge variant="outline">{typeInfo.label}</Badge>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {groupAccounts.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            No hay cuentas en este grupo
                          </p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-24">Codigo</TableHead>
                                <TableHead>Nombre</TableHead>
                                <TableHead className="text-right">Debe</TableHead>
                                <TableHead className="text-right">Haber</TableHead>
                                <TableHead className="text-right">Saldo</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groupAccounts.map((account) => (
                                <TableRow key={account.id} className={account.is_header ? 'bg-muted/30 font-medium' : ''}>
                                  <TableCell className="font-mono">
                                    {account.code}
                                    {account.is_header && <Badge variant="secondary" className="ml-2 text-xs">Cabecera</Badge>}
                                  </TableCell>
                                  <TableCell>{account.name}</TableCell>
                                  <TableCell className="text-right font-mono">
                                    {account.balance_debit > 0 ? formatCurrency(account.balance_debit) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">
                                    {account.balance_credit > 0 ? formatCurrency(account.balance_credit) : '-'}
                                  </TableCell>
                                  <TableCell className={`text-right font-mono ${account.balance > 0 ? 'text-green-600' : account.balance < 0 ? 'text-red-600' : ''}`}>
                                    {account.balance !== 0 ? formatCurrency(account.balance) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {account.is_active ? (
                                      <Badge className="bg-green-100 text-green-700">Activa</Badge>
                                    ) : (
                                      <Badge variant="secondary">Inactiva</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(account)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              {editingAccount?.id ? "Editar Cuenta" : "Nueva Cuenta"}
            </DialogTitle>
          </DialogHeader>

          {editingAccount && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Codigo *</Label>
                  <Input
                    value={editingAccount.code || ''}
                    onChange={(e) => setEditingAccount({ ...editingAccount, code: e.target.value })}
                    placeholder="430"
                    className="font-mono"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={editingAccount.name || ''}
                    onChange={(e) => setEditingAccount({ ...editingAccount, name: e.target.value })}
                    placeholder="Clientes"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de cuenta</Label>
                  <Select
                    value={editingAccount.account_type}
                    onValueChange={(v) => setEditingAccount({ ...editingAccount, account_type: v as any })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Naturaleza</Label>
                  <Select
                    value={editingAccount.is_debit_normal ? 'debit' : 'credit'}
                    onValueChange={(v) => setEditingAccount({ ...editingAccount, is_debit_normal: v === 'debit' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="debit">Deudora (Debe)</SelectItem>
                      <SelectItem value="credit">Acreedora (Haber)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripcion</Label>
                <Textarea
                  value={editingAccount.description || ''}
                  onChange={(e) => setEditingAccount({ ...editingAccount, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingAccount.is_header}
                    onChange={(e) => setEditingAccount({ ...editingAccount, is_header: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Cuenta de cabecera (agrupacion)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingAccount.is_active !== false}
                    onChange={(e) => setEditingAccount({ ...editingAccount, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Activa</span>
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
